import React, { useMemo, useState, useEffect } from 'react';
import {
  addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth,
  isWithinInterval, parseISO, startOfMonth, startOfWeek, isBefore, isValid,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Flag, Users } from 'lucide-react';

// ─── Range picker for booking leave ─────────────────────────────────────────
// Replaces the browser's date inputs (which accepted six-digit years and
// showed no holidays). Click the first day, then the last; a single day is two
// clicks on the same date (or one, for half days and custom hours).
//   • weekends and the person's non-working days are muted and hatched
//   • public holidays are flagged, with the name on hover
//   • days when colleagues in the same department are off show a count
//   • the selection can't end before it starts, and the range is spelt out
// Props:
//   from, to            'yyyy-MM-dd' or ''
//   single              pick one day only (half days, custom hours)
//   onChange({ from, to })
//   holidays            [{ date, name }]
//   isWorkingDay(date)  the person's schedule (default Mon–Fri)
//   clashesOn(date)     [{ name, label }] colleagues off that day
//   holidayWord         "Public holiday" / "Bank holiday"
//   minDate             earliest selectable date (Date) — e.g. today for members
const iso = (d) => format(d, 'yyyy-MM-dd');
const parse = (s) => {
  if (!s) return null;
  const d = parseISO(String(s).slice(0, 10));
  return isValid(d) ? d : null;
};
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function DateRangePicker({
  from, to, single = false, onChange, holidays = [], isWorkingDay, clashesOn, holidayWord = 'Public holiday', minDate = null,
}) {
  const start = parse(from);
  const end = parse(to);
  const [view, setView] = useState(() => startOfMonth(start ?? new Date()));
  const [hover, setHover] = useState(null);
  // Waiting for the second click?
  const picking = !single && !!start && !end;

  useEffect(() => { if (start && !isSameMonth(start, view) && !isSameMonth(addMonths(view, 1), start)) setView(startOfMonth(start)); }, [from]); // eslint-disable-line react-hooks/exhaustive-deps

  const holidayByDate = useMemo(() => {
    const m = new Map();
    holidays.forEach((h) => { const d = String(h.date ?? '').slice(0, 10); if (d && !m.has(d)) m.set(d, h.name); });
    return m;
  }, [holidays]);

  const works = (d) => (isWorkingDay ? isWorkingDay(d) : ![0, 6].includes(d.getDay()));

  function pick(d) {
    if (minDate && isBefore(d, minDate) && !isSameDay(d, minDate)) return;
    if (single) { onChange({ from: iso(d), to: iso(d) }); return; }
    if (!start || (start && end)) { onChange({ from: iso(d), to: '' }); return; }
    if (isBefore(d, start)) { onChange({ from: iso(d), to: '' }); return; } // earlier day: start again from there
    onChange({ from: iso(start), to: iso(d) });
  }

  function onKey(e, d) {
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    if (step) {
      e.preventDefault();
      const next = new Date(d); next.setDate(next.getDate() + step);
      if (!isSameMonth(next, view) && !isSameMonth(next, addMonths(view, 1))) setView(startOfMonth(next));
      setTimeout(() => document.querySelector(`[data-day="${iso(next)}"]`)?.focus(), 0);
    }
  }

  const rangeEnd = end ?? (picking && hover && !isBefore(hover, start) ? hover : start);

  function renderMonth(month) {
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
    });
    return (
      <div className="drp-month" key={format(month, 'yyyy-MM')}>
        <div className="drp-title">{format(month, 'MMMM yyyy')}</div>
        <div className="drp-grid" role="grid" aria-label={format(month, 'MMMM yyyy')}>
          {WEEKDAYS.map((w) => <div key={w} className="drp-wd" role="columnheader">{w.slice(0, 2)}</div>)}
          {days.map((d) => {
            const inMonth = isSameMonth(d, month);
            if (!inMonth) return <div key={iso(d)} className="drp-cell drp-out" aria-hidden="true" />;
            const key = iso(d);
            const hol = holidayByDate.get(key);
            const off = !works(d);
            const disabled = !!(minDate && isBefore(d, minDate) && !isSameDay(d, minDate));
            const selected = start && rangeEnd && isWithinInterval(d, { start, end: isBefore(rangeEnd, start) ? start : rangeEnd });
            const isStart = start && isSameDay(d, start);
            const isEnd = rangeEnd && isSameDay(d, rangeEnd);
            const clashes = clashesOn ? clashesOn(d) : [];
            const label = [
              format(d, 'EEEE d MMMM yyyy'),
              hol ? `${holidayWord}: ${hol}` : null,
              off && !hol ? 'not a working day' : null,
              clashes.length ? `${clashes.length} colleague${clashes.length === 1 ? '' : 's'} off: ${clashes.map((c) => c.name).join(', ')}` : null,
            ].filter(Boolean).join(' · ');
            return (
              <button
                key={key}
                type="button"
                data-day={key}
                role="gridcell"
                aria-selected={!!selected}
                aria-label={label}
                title={label}
                disabled={disabled}
                onClick={() => pick(d)}
                onKeyDown={(e) => onKey(e, d)}
                onMouseEnter={() => setHover(d)}
                className={[
                  'drp-cell',
                  off ? 'drp-off' : '',
                  hol ? 'drp-hol' : '',
                  selected ? 'drp-sel' : '',
                  isStart ? 'drp-start' : '',
                  isEnd ? 'drp-end' : '',
                  isSameDay(d, new Date()) ? 'drp-today' : '',
                ].join(' ')}
              >
                <span className="drp-num">{format(d, 'd')}</span>
                {hol && <Flag size={9} className="drp-flag" aria-hidden="true" />}
                {clashes.length > 0 && !off && !hol && <span className="drp-dot" aria-hidden="true">{clashes.length}</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="drp" onMouseLeave={() => setHover(null)}>
      <style>{`
        .drp { border: 1px solid var(--field-border); border-radius: 14px; background: var(--field); padding: 10px 10px 8px; }
        .drp-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
        .drp-nav button { width: 30px; height: 30px; border-radius: 9px; border: 1px solid var(--border); background: var(--surface); color: var(--text-2); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
        .drp-nav button:hover { background: var(--hover); }
        .drp-months { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        @media (max-width: 620px) { .drp-months { grid-template-columns: 1fr; } .drp-months > .drp-month:nth-child(2) { display: none; } }
        .drp-title { text-align: center; font-size: 13px; font-weight: 700; color: var(--text); margin: -26px 0 8px; pointer-events: none; }
        .drp-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; }
        .drp-wd { text-align: center; font-size: 10.5px; font-weight: 700; color: var(--text-muted); padding: 2px 0 4px; text-transform: uppercase; letter-spacing: .04em; }
        .drp-cell { position: relative; height: 34px; border: none; border-radius: 9px; background: transparent; color: var(--text); font-size: 12.5px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
        .drp-cell:hover:not(:disabled) { background: var(--hover); }
        .drp-cell:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
        .drp-out { cursor: default; }
        .drp-off { color: var(--text-faint); background-image: repeating-linear-gradient(135deg, transparent 0 4px, var(--border-soft) 4px 5px); }
        .drp-hol { color: var(--holiday-ink, var(--text)); background: var(--holiday-bg, #FFF4D6); }
        .drp-flag { position: absolute; top: 3px; right: 4px; color: var(--holiday-ink, #8A5F00); }
        .drp-today .drp-num { text-decoration: underline; text-underline-offset: 3px; text-decoration-thickness: 2px; }
        .drp-sel { background: var(--tint); color: var(--text); border-radius: 0; }
        .drp-sel.drp-off, .drp-sel.drp-hol { color: var(--text-muted); }
        .drp-start, .drp-end { background: #FFC81E !important; color: #1C1A14 !important; font-weight: 700; }
        .drp-start { border-radius: 9px 0 0 9px; } .drp-end { border-radius: 0 9px 9px 0; } .drp-start.drp-end { border-radius: 9px; }
        .drp-cell:disabled { color: var(--text-faint); opacity: .45; cursor: not-allowed; }
        .drp-dot { position: absolute; bottom: 2px; right: 3px; min-width: 13px; height: 13px; padding: 0 3px; border-radius: 7px; background: var(--warn-bg); color: var(--warn-text); font-size: 9px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
        .drp-legend { display: flex; flex-wrap: wrap; gap: 4px 12px; margin-top: 8px; font-size: 11px; color: var(--text-muted); }
        .drp-legend span { display: inline-flex; align-items: center; gap: 4px; }
        .drp-sw { width: 11px; height: 11px; border-radius: 3px; display: inline-block; }
      `}</style>
      <div className="drp-nav">
        <button type="button" onClick={() => setView((v) => addMonths(v, -1))} aria-label="Previous month"><ChevronLeft size={15} /></button>
        <button type="button" onClick={() => setView((v) => addMonths(v, 1))} aria-label="Next month"><ChevronRight size={15} /></button>
      </div>
      <div className="drp-months">
        {renderMonth(view)}
        {renderMonth(addMonths(view, 1))}
      </div>
      <div className="drp-legend" aria-hidden="true">
        <span><span className="drp-sw" style={{ background: 'var(--holiday-bg, #FFF4D6)', border: '1px solid var(--holiday-edge, #F0D58A)' }} />{holidayWord}</span>
        <span><span className="drp-sw" style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 3px, var(--border) 3px 4px)', border: '1px solid var(--border)' }} />Not working</span>
        {clashesOn && <span><Users size={11} />Colleagues off</span>}
        {picking && <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Now pick the last day</span>}
      </div>
    </div>
  );
}

export default DateRangePicker;